import { type PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';

import type * as ApiModels from '@/typings/api-models';

export type WorkflowCollaborator = Pick<
  ApiModels.UserWorkflowCollaborator,
  | 'id'
  | 'source'
  | 'canAssign'
  | 'canInviteCollaborators'
  | 'canReceiveCustomerNotifications'
  | 'canHavePermissionsChanged'
  | 'name'
  | 'color'
  | 'logo'
  | 'email'
  | 'roleLabel'
  | 'personId'
  | 'contactPhoneNumber'
>;

type UserWorkflowContextValue = {
  assignedAttorneyPersonId: string | null;
  collaborators: Array<WorkflowCollaborator>;
  setCollaborators: (collabs: WorkflowCollaborator[]) => void;
};

const UserWorkflowContext = createContext<UserWorkflowContextValue>({
  assignedAttorneyPersonId: null,
  collaborators: [],
  setCollaborators: () => {},
});

const UserWorkflowContextProvider: React.FC<
  PropsWithChildren<{
    assignedAttorneyPersonId?: string | null;
    collaborators: Array<WorkflowCollaborator>;
  }>
> = ({ children, assignedAttorneyPersonId = null, collaborators: originCollaborators }) => {
  const [collaborators, setCollaborators] = useState<WorkflowCollaborator[]>(originCollaborators);

  const values = useMemo(
    () => ({
      assignedAttorneyPersonId,
      collaborators,
      setCollaborators,
    }),
    [assignedAttorneyPersonId, collaborators],
  );

  useEffect(() => {
    setCollaborators(originCollaborators);
  }, [originCollaborators]);

  return <UserWorkflowContext.Provider value={values}>{children}</UserWorkflowContext.Provider>;
};

const useUserWorkflowContext = (): UserWorkflowContextValue => useContext(UserWorkflowContext);

export { UserWorkflowContextProvider };
export default useUserWorkflowContext;
